import { useSyncExternalStore, type RefObject } from 'react';
import { Undo2, Eye, Minus, Plus } from 'lucide-react';
import type { GameEngine } from '@/game/engine';
import { useLocale } from '@/i18n';
import { adjacentGameZoom, MIN_GAME_ZOOM, MAX_GAME_ZOOM, type CameraControlsSnapshot } from '@/game/town-camera';
import { copperButton } from '@/shared/ui/game-theme';

const inactiveCamera: CameraControlsSnapshot = { zoom: MAX_GAME_ZOOM, minimum: MIN_GAME_ZOOM, inTown: false, overview: false };
const getInactiveCamera = () => inactiveCamera;
const subscribeInactiveCamera = () => () => {};

/** Camera commands are presentation only; movement continues on the server. */
export function GameCameraControls({ engineRef }: { engineRef: RefObject<GameEngine | null> }) {
  const { locale } = useLocale();
  const ru = locale === 'ru';
  const engine = engineRef.current;
  const { zoom, inTown, overview } = useSyncExternalStore(
    engine?.subscribeCameraControls ?? subscribeInactiveCamera,
    engine?.getCameraControlsSnapshot ?? getInactiveCamera,
    getInactiveCamera,
  );
  const buttonStyle = { ...copperButton, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    width: 34, minHeight: 34, padding: 4 };
  const change = (direction: 'in' | 'out') => {
    const current = engineRef.current;
    if (!current) return;
    const next = adjacentGameZoom(current.camera.zoom, direction);
    if (next !== null) current.setCameraZoom(next);
  };
  return <div role="group" aria-label={ru ? 'Масштаб камеры' : 'Camera zoom'} style={{ display: 'flex', gap: 3 }}>
    {zoom !== MAX_GAME_ZOOM && <button type="button" style={buttonStyle}
      title={ru ? 'Вернуть обычный масштаб' : 'Restore default zoom'}
      aria-label={ru ? 'Вернуть обычный масштаб' : 'Restore default zoom'}
      disabled={!engine || zoom === MAX_GAME_ZOOM}
      onClick={() => { const current = engineRef.current; if (current && current.camera.zoom !== MAX_GAME_ZOOM) current.setCameraZoom(MAX_GAME_ZOOM); }}><Undo2 size={16} /></button>}
    <button type="button" style={buttonStyle} title={ru ? 'Отдалить' : 'Zoom out'} aria-label={ru ? 'Отдалить' : 'Zoom out'}
      disabled={!engine || adjacentGameZoom(zoom, 'out') === null} onClick={() => change('out')}><Minus size={16} /></button>
    <button type="button" style={buttonStyle} title={ru ? 'Приблизить' : 'Zoom in'} aria-label={ru ? 'Приблизить' : 'Zoom in'}
      disabled={!engine || adjacentGameZoom(zoom, 'in') === null} onClick={() => change('in')}><Plus size={16} /></button>

    {inTown && <button type="button" style={buttonStyle} title={ru ? 'Показать весь город' : 'Show whole town'}
      aria-label={ru ? 'Показать весь город' : 'Show whole town'} aria-pressed={overview}
      onClick={() => { engineRef.current?.showTownOverview(); }}><Eye size={16} /></button>}
  </div>;
}
